import type { FC, FormEvent, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Locale } from '@/constants/i18n'
import type { ProfileErrors, ProfileValues } from '@/components/forms/profile-fields'
import type { TurnstileHandle } from '@/components/forms/turnstile'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useRef, useState } from 'react'
import { Seo } from '@/components/generals/seo'
import { AuthCard, AuthShell } from '@/components/auth/auth-shell'
import { SocialButtons } from '@/components/auth/social-buttons'
import { ProfileFields, emptyProfile, validateProfile } from '@/components/forms/profile-fields'
import { PasswordInput } from '@/components/forms/password-input'
import { Turnstile } from '@/components/forms/turnstile'
import { Button } from '@/components/primitives/button'
import { Field, Notice, TextInput } from '@/components/primitives/field'
import { LogoBadge } from '@/components/brand/logo'
import { useI18n } from '@/i18n/context'
import { errorText } from '@/i18n/errors'
import { apiFetch } from '@/lib/api-client'
import { isEmail, passwordProblem, safeNext } from '@/lib/validation'
import { cn } from '@/lib/utils'
import { getSessionUser } from '@/lib/server/session'
import { nextStepFor, resolveLocale } from '@/lib/server/gate'
import { turnstileSiteKey } from '@/lib/server/integrations'
import { maybeSendLaunchEmail } from '@/lib/server/launch'

type Tab = 'register' | 'login'

interface AuthPageProps {
    locale: Locale
    siteKey: string | null
    initialTab: Tab
    errorCode: string | null
    errorProvider: string | null
    next: string
}

const PROVIDERS: Record<string, string> = { google: 'Google', facebook: 'Facebook', apple: 'Apple' }

const Divider: FC<{ label: string }> = ({ label }): ReactNode => (
    <div className='my-6 flex items-center gap-4 text-xs uppercase tracking-[0.3em] text-muted-foreground'>
        <span className='h-px flex-1 bg-gradient-to-r from-transparent to-gold/30 rtl:bg-gradient-to-l' />
        {label}
        <span className='h-px flex-1 bg-gradient-to-l from-transparent to-gold/30 rtl:bg-gradient-to-r' />
    </div>
)

const AuthPage: FC<AuthPageProps> = ({ siteKey, initialTab, errorCode, errorProvider, next }): ReactNode => {
    const { m, locale } = useI18n()
    const router = useRouter()
    const turnstile = useRef<TurnstileHandle>(null)

    const [tab, setTab] = useState<Tab>(initialTab)
    const [showEmailForm, setShowEmailForm] = useState(false)
    const [token, setToken] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)
    const [formError, setFormError] = useState<string | null>(null)

    const [profile, setProfile] = useState<ProfileValues>(emptyProfile)
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [passwordConfirm, setPasswordConfirm] = useState('')
    const [errors, setErrors] = useState<ProfileErrors & { email?: string, password?: string, passwordConfirm?: string }>({})

    const providerName = errorProvider ? PROVIDERS[errorProvider] ?? '' : ''
    const queryError = errorCode ? errorText(m, errorCode, { provider: providerName }) : null

    const switchTab = (value: Tab) => {
        setTab(value)
        setErrors({})
        setFormError(null)
        void router.replace({ pathname: '/auth', query: value === 'login' ? { tab: 'login', ...(next !== '/' ? { next } : {}) } : (next !== '/' ? { next } : {}) }, undefined, { shallow: true, scroll: false })
    }

    const clearError = (key: string) => setErrors(current => ({ ...current, [key]: undefined }))

    const passwordError = (value: string): string | undefined => {
        const problem = passwordProblem(value)

        if (problem === 'missing') return m.errors.required
        if (problem === 'short') return m.errors.passwordShort
        if (problem === 'weak') return m.errors.passwordWeak

        return undefined
    }

    const handleServerError = (data: { error?: string, field?: string, provider?: string }) => {
        const message = errorText(m, data.error, { provider: data.provider ?? '' })

        if (data.field) {
            const field = data.field === 'firstName' || data.field === 'lastName' || data.field === 'nationality' || data.field === 'phone' || data.field === 'email' || data.field === 'password' || data.field === 'passwordConfirm' ? data.field : null

            if (field) setErrors(current => ({ ...current, [field]: message }))
            else setFormError(message)
        }
        else setFormError(message)

        turnstile.current?.reset()
    }

    const submitRegister = async (event: FormEvent) => {
        event.preventDefault()
        setFormError(null)

        const found: typeof errors = validateProfile(profile, m.errors)

        if (!email.trim()) found.email = m.errors.required
        else if (!isEmail(email)) found.email = m.errors.invalidEmail

        found.password = passwordError(password)

        if (!passwordConfirm) found.passwordConfirm = m.errors.required
        else if (passwordConfirm !== password) found.passwordConfirm = m.errors.passwordMismatch

        setErrors(found)

        if (Object.values(found).some(Boolean)) return
        if (siteKey && !token) {
            setFormError(m.errors.securityPending)

            return
        }

        setBusy(true)

        const result = await apiFetch<{ redirect?: string }>('/api/auth/register', { ...profile, email, password, passwordConfirm, turnstileToken: token, locale })

        if (result.ok && result.data.redirect) {
            await router.push(result.data.redirect)

            return
        }

        setBusy(false)
        handleServerError(result.data)
    }

    const submitLogin = async (event: FormEvent) => {
        event.preventDefault()
        setFormError(null)

        const found: typeof errors = {}

        if (!email.trim()) found.email = m.errors.required
        else if (!isEmail(email)) found.email = m.errors.invalidEmail

        if (!password) found.password = m.errors.required

        setErrors(found)

        if (Object.values(found).some(Boolean)) return
        if (siteKey && !token) {
            setFormError(m.errors.securityPending)

            return
        }

        setBusy(true)

        const result = await apiFetch<{ redirect?: string }>('/api/auth/login', { email, password, turnstileToken: token, next })

        if (result.ok && result.data.redirect) {
            await router.push(result.data.redirect)

            return
        }

        setBusy(false)
        handleServerError(result.data)
    }

    return (
        <AuthShell>
            <Seo title={tab === 'login' ? m.auth.tabLogin : m.auth.tabRegister} description={m.meta.description} />

            <AuthCard>
                <div className='flex flex-col items-center text-center'>
                    <LogoBadge size={92} priority className='mb-5' />

                    <h1 className='font-heading text-[2.1rem] font-semibold leading-tight sm:text-[2.5rem]'>
                        <span className='sv-gold-text'>{m.auth.title}</span>
                    </h1>

                    <p className='mt-3 max-w-md text-[15px] leading-relaxed text-muted-foreground'>
                        {m.auth.subtitle}
                    </p>
                </div>

                <div className='mt-7 grid grid-cols-2 gap-1 rounded-2xl border border-gold/15 bg-black/25 p-1' role='tablist'>
                    {(['register', 'login'] as Array<Tab>).map(value => (
                        <button
                            key={value}
                            type='button'
                            role='tab'
                            aria-selected={tab === value}
                            onClick={() => switchTab(value)}
                            className={cn(
                                'h-11 rounded-xl text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold',
                                tab === value ? 'bg-gold text-primary-foreground shadow-[0_8px_24px_-12px_rgba(212,175,90,0.8)]' : 'text-muted-foreground hover:text-foreground'
                            )}
                        >
                            {value === 'register' ? m.auth.tabRegister : m.auth.tabLogin}
                        </button>
                    ))}
                </div>

                {queryError && <Notice tone='error' className='mt-6'>{queryError}</Notice>}

                <div className='mt-6'>
                    <SocialButtons
                        next={next}
                        showEmail={tab === 'register'}
                        emailActive={showEmailForm}
                        onEmail={() => setShowEmailForm(current => !current)}
                    />

                    <p className='mt-3 text-center text-xs leading-relaxed text-muted-foreground'>{m.auth.socialNote}</p>
                </div>

                {tab === 'register' && showEmailForm && (
                    <form onSubmit={submitRegister} noValidate className='mt-8 space-y-4 animate-in fade-in slide-in-from-top-2 duration-300'>
                        <Divider label={m.auth.emailRegister} />

                        <ProfileFields
                            idPrefix='register'
                            values={profile}
                            errors={errors}
                            onChange={(values, changed) => {
                                setProfile(values)
                                clearError(changed)
                            }}
                        />

                        <Field id='register-email' label={m.auth.email} error={errors.email}>
                            <TextInput
                                id='register-email'
                                type='email'
                                inputMode='email'
                                autoComplete='email'
                                value={email}
                                invalid={Boolean(errors.email)}
                                onChange={event => {
                                    setEmail(event.target.value)
                                    clearError('email')
                                }}
                            />
                        </Field>

                        <div className='grid gap-4 sm:grid-cols-2'>
                            <Field id='register-password' label={m.auth.password} error={errors.password} hint={m.auth.passwordHint} showIcon={false}>
                                <PasswordInput
                                    id='register-password'
                                    value={password}
                                    invalid={Boolean(errors.password)}
                                    autoComplete='new-password'
                                    onChange={value => {
                                        setPassword(value)
                                        clearError('password')
                                    }}
                                />
                            </Field>

                            <Field id='register-password-confirm' label={m.auth.passwordConfirm} error={errors.passwordConfirm} showIcon={false}>
                                <PasswordInput
                                    id='register-password-confirm'
                                    value={passwordConfirm}
                                    invalid={Boolean(errors.passwordConfirm)}
                                    autoComplete='new-password'
                                    onChange={value => {
                                        setPasswordConfirm(value)
                                        clearError('passwordConfirm')
                                    }}
                                />
                            </Field>
                        </div>

                        <Turnstile ref={turnstile} siteKey={siteKey} action='register' locale={locale} onToken={setToken} />

                        {formError && <Notice tone='error'>{formError}</Notice>}

                        <Button type='submit' variant='success' size='lg' loading={busy} className='w-full'>
                            {m.auth.submitRegister}
                        </Button>

                        <p className='text-center text-xs leading-relaxed text-muted-foreground'>
                            {m.auth.privacyPrefix}{' '}
                            <Link href='/privacy' target='_blank' className='font-medium text-gold underline-offset-4 hover:underline'>{m.auth.privacyLink}</Link>
                            {m.auth.privacySuffix}
                        </p>
                    </form>
                )}

                {tab === 'login' && (
                    <form onSubmit={submitLogin} noValidate className='space-y-4'>
                        <Divider label={m.common.or} />

                        <Field id='login-email' label={m.auth.email} error={errors.email}>
                            <TextInput
                                id='login-email'
                                type='email'
                                inputMode='email'
                                autoComplete='email'
                                value={email}
                                invalid={Boolean(errors.email)}
                                onChange={event => {
                                    setEmail(event.target.value)
                                    clearError('email')
                                }}
                            />
                        </Field>

                        <Field id='login-password' label={m.auth.password} error={errors.password} showIcon={false}>
                            <PasswordInput
                                id='login-password'
                                value={password}
                                invalid={Boolean(errors.password)}
                                autoComplete='current-password'
                                onChange={value => {
                                    setPassword(value)
                                    clearError('password')
                                }}
                            />
                        </Field>

                        <div className='flex justify-end'>
                            <Link href={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ''}`} className='text-sm font-medium text-gold underline-offset-4 hover:underline'>
                                {m.auth.forgot}
                            </Link>
                        </div>

                        <Turnstile ref={turnstile} siteKey={siteKey} action='login' locale={locale} onToken={setToken} />

                        {formError && <Notice tone='error'>{formError}</Notice>}

                        <Button type='submit' variant='success' size='lg' loading={busy} className='w-full'>
                            {m.auth.submitLogin}
                        </Button>
                    </form>
                )}

                {siteKey && <p className='mt-5 text-center text-[11px] text-muted-foreground/80'>{m.common.securityNote}</p>}

                {tab === 'register' && !showEmailForm && (
                    <p className='mt-6 text-center text-xs text-muted-foreground'>
                        {m.auth.privacyPrefix}{' '}
                        <Link href='/privacy' className='font-medium text-gold underline-offset-4 hover:underline'>{m.auth.privacyLink}</Link>
                        {m.auth.privacySuffix}
                    </p>
                )}
            </AuthCard>
        </AuthShell>
    )
}

export const getServerSideProps: GetServerSideProps<AuthPageProps> = async ctx => {
    maybeSendLaunchEmail(ctx.req)

    const next = safeNext(ctx.query.next, '/')
    const user = await getSessionUser(ctx.req, ctx.res)

    if (user) return { redirect: { destination: nextStepFor(user, next), permanent: false } }

    const error = typeof ctx.query.error === 'string' ? ctx.query.error : null
    const provider = typeof ctx.query.provider === 'string' ? ctx.query.provider : null

    return {
        props: {
            locale: resolveLocale(ctx.req),
            siteKey: turnstileSiteKey(),
            initialTab: ctx.query.tab === 'login' ? 'login' : 'register',
            errorCode: error,
            errorProvider: provider,
            next
        }
    }
}

export default AuthPage
